/**
 * Handles the process of remembering a password by getting the user email address and sending
 * him/her an email.
 */
class RememberPasswordContainer {
  /**
   * @param {ngQ}                   $q                To create an already rejected promise. This
   *                                                  is used when the request is made while
   *                                                  another is already being processed.
   * @param {FrontendConfiguration} appConfiguration  To get the Olapic's privacy policy URL.
   * @param {Auth}                  auth              To make the request to reset the password.
   * @param {AuthRoutesManager}     authRoutesManager To get the route to login screen and use it
   *                                                  on the _"go back_ link.
   * @param {SSOState}              ssoState          To get the settings of the SSO client
   *                                                  implementation. The request may include an
   *                                                  application ID or a permissions scope ID in
   *                                                  order to redirect the user to the right
   *                                                  application after heshe resets the password.
   */
  constructor($q, appConfiguration, auth, authRoutesManager, ssoState) {
    'ngInject';

    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the `auth` service.
     * @type {Auth}
     */
    this.auth = auth;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesMan}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * A local reference for the `ssoState` service.
     * @type {SSOState}
     */
    this.ssoState = ssoState;
    /**
     * The Olapic's privacy policy URL, to show on a link.
     * @type {string}
     */
    this.privacyPolicyUrl = appConfiguration.privacyPolicyURL;
    /**
     * To URL for the _"go back"_ link. When the container gets mounted, it will be completed
     * with the login screen URL.
     * @type {string}
     */
    this.goBackUrl = '';
    /**
     * Once the information is submitted and validated, in case of error, this property will be
     * used to store the message and send it to the child component.
     * @type {string}
     */
    this.error = '';
    /**
     * Whether or not the container is processing the form.
     * @type {boolean}
     */
    this.loading = false;
    /**
     * Whether or not the process finished.
     * @type {boolean}
     */
    this.success = false;
  }
  /**
   * When the component gets mounted, validate if the `state` binding is `true` and build the
   * _"go back"_ link URL.
   */
  $onInit() {
    this.goBackUrl = this.authRoutesManager.login(!!this.state);
  }
  /**
   * Processes the form. This is the callback sent to the child component.
   * @param {string} email The user email address.
   * @return {Promise<undefined,Error>}
   */
  rememberPassword(email) {
    let result;
    if (this.loading) {
      // Reject the promise if the container is already processing the form.
      result = this.$q.reject(new Error('The request is in progress'));
    } else {
      // Turn on the _"processing flag"_.
      this.loading = true;
      // Define the options dictionary that will be sent to the method that makes the request.
      const options = {};
      if (this.state) {
        // If there was an state, get its contents.
        const state = this.ssoState.getState();
        /**
         * Set the application name and the permission scope from the state on the options that will
         * be sent to the API. This is so the backend can build a link for the user to return to
         * the right implementation after resetting his/her password.
         */
        options.application = state.application;
        options.accountScope = state.accountScope;
      }
      // Make the request to reset the password.
      result = this.auth.rememberPassword(email, options)
      .then(() => {
        // If everything went well, mark the `success` flag.
        this.success = true;
      })
      .catch((error) => {
        // If something went wrong, get the error message for the child component to show.
        this.error = error.message;
      })
      .then(() => {
        // Turn off the _"processing flag"_.
        this.loading = false;
      });
    }
    // Return the promise.
    return result;
  }
}
/**
 * Shows and handles the logic of the _"remember your password"_ screen.
 * @ngesdoc component
 * @ngname rememberPasswordContainer
 * @ngcontroller {@link RememberPasswordContainer}
 * @ngcontainer
 * @ngmodule auth
 * @ngbinding {?boolean} state Whether a state is currently saved or not. This is `true` when the
 *                             user arrives to this container after the SSO client parameters were
 *                             processed; and `false` if the user just wrote the URL of the app.
 */
export default {
  controller: RememberPasswordContainer,
  template: `
    <remember-password
      error="$ctrl.error"
      loading="$ctrl.loading"
      success="$ctrl.success"
      on-remember="$ctrl.rememberPassword(email)"
      go-back-url="$ctrl.goBackUrl"
      privacy-policy-url="$ctrl.privacyPolicyUrl"
    ></remember-password>
  `,
  bindings: {
    state: '<',
  },
};
